type HastNode = any;

type HastElement = {
    type: "element";
    tagName: string;
    properties?: Record<string, any>;
    children?: HastNode[];
};

type HastText = {
    type: "text";
    value: string;
};

type HastParentLike = {
    children?: HastNode[];
};

function isElementNode(node: HastNode): node is HastElement {
    return Boolean(node && typeof node === "object" && (node as HastElement).type === "element");
}

function hasChildren(node: HastNode): node is HastParentLike {
    return Boolean(node && typeof node === "object" && Array.isArray((node as HastParentLike).children));
}

function getChildren(node: HastNode): HastNode[] {
    return hasChildren(node) ? (node.children as HastNode[]) : [];
}

const LANGUAGE_CLASS = "language-tixu-quote";

const TITLE_MARKER = "[title]";
const CONTENT_MARKER = "[content]";

export default function rehypeTixuQuote() {
    function transform(parent: HastNode) {
        const children: HastNode[] = getChildren(parent);
        if (!children.length) return;

        let index = 0;
        while (index < children.length) {
            const currentNode = children[index];
            if (!isElementNode(currentNode)) {
                index++;
                continue;
            }
            const current = currentNode;

            if (isQuoteCodeBlock(current)) {
                const codeElement = (current.children ?? [])[0] as HastElement;
                const rawText = extractTextContent(codeElement);
                const parsed = parseBlock(rawText ?? "");

                children.splice(index, 1, createBigQuoteNode(parsed));
                index++;
                continue;
            }

            const currentChildren = getChildren(current);
            if (currentChildren.length > 0) {
                transform(current);
            }

            index++;
        }
    }

    return (tree: HastNode) => {
        transform(tree);
    };
}

type ParsedQuote = {
    title: string;
    content: string;
};

function isQuoteCodeBlock(node: HastElement | undefined): node is HastElement {
    if (!node || node.type !== "element" || node.tagName !== "pre") return false;
    const firstChild = node.children?.[0] as HastElement | undefined;
    if (!firstChild || firstChild.type !== "element" || firstChild.tagName !== "code") return false;
    const className: unknown = firstChild.properties?.className;
    const classes = Array.isArray(className)
        ? className
        : typeof className === "string"
            ? className.split(" ")
            : [];
    return classes.includes(LANGUAGE_CLASS);
}

function extractTextContent(node: HastElement | undefined): string {
    if (!node?.children?.length) return "";
    return node.children
        .map((child: HastNode) => {
            if (typeof child?.value === "string") return child.value;
            if (typeof child === "string") return child;
            if ((child as HastText)?.type === "text") return (child as HastText).value;
            return "";
        })
        .join("");
}

function parseBlock(block: string): ParsedQuote {
    const lines = block.split(/\r?\n/);

    let title = "";
    let content = "";
    let mode: "idle" | "title" | "content" = "idle";

    for (const line of lines) {
        const trimmed = line.trim();

        if (!trimmed) {
            if (mode === "title") {
                title += "\n";
            } else if (mode === "content") {
                content += "\n";
            }
            continue;
        }

        if (trimmed.toLowerCase() === TITLE_MARKER) {
            mode = "title";
            title = "";
            continue;
        }

        if (trimmed.toLowerCase() === CONTENT_MARKER) {
            mode = "content";
            content = "";
            continue;
        }

        if (mode === "title") {
            title += `${title ? "\n" : ""}${line}`;
        } else if (mode === "content") {
            content += `${content ? "\n" : ""}${line}`;
        }
    }

    return {
        title: title.trim(),
        content: content.trim()
    };
}

function createBigQuoteNode(quote: ParsedQuote): HastElement {
    return {
        type: "element",
        tagName: "blockquote",
        properties: {
            "data-tixu-quote": "true",
            "data-title": quote.title,
            "data-content": quote.content
        },
        children: []
    };
}

